import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import { LineChart } from 'chartist';
import UrlHelpers from '../../shared/url_helpers';
import { APIRequest, UserStore } from '@reverbdotcom/discovery-ui';

import Money from '../../shared/money';
import classNames from 'classnames';

const MIN_SUMMARIES = 3;
const NUMBER_OF_MONTHS_DEFAULT = 12;
const DEFAULT_CONDITION = 'used';
const COMPARISON_SHOPPING_PAGE = 'comparisonShoppingPage';

class PriceGuideChart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {};

    this.chart = null;

    // Creating unique chart selectors allows us to use
    // multiple charts on a page, such as a blog post
    this.chartSelector = `[data-chart=line-chart-${this.props.objectId}]`;
  }

  componentDidMount() {
    if (this.props.loadChart) {
      this.fetchAndRenderChart();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.loadChart && !prevProps.loadChart) {
      this.fetchAndRenderChart();
    }
  }

  get params() {
    return {
      number_of_months: NUMBER_OF_MONTHS_DEFAULT,
      condition: DEFAULT_CONDITION,
    };
  }

  get url() {
    if (this.props.objectType === COMPARISON_SHOPPING_PAGE) {
      return UrlHelpers
        .comparisonShoppinagPageTransactionsSummaryPath(this.props.objectId, this.params);
    }

    return UrlHelpers.priceGuideTransactionsSummaryPath(this.props.objectId, this.params);
  }

  get userCurrency() {
    return UserStore.getState().currency;
  }

  formatPrice(amountCents) {
    const money = new Money(amountCents, this.userCurrency);
    return money.toStringNoCents();
  }

  buildChart(model) {
    const priceFinals = [];
    const labels = [];

    model.summaries.forEach((summary) => {
      const shortMonthShortYear = 'MMM YY';
      labels.push(moment(summary.date).format(shortMonthShortYear));
      const priceFinal = summary.average_price_final.amount_cents;

      priceFinals.push(priceFinal);
    });

    this.chart = new LineChart(this.chartSelector, {
      labels,
      series: [
        { name: I18n.t('js.transactionHistory.soldPrice'), data: priceFinals },
      ],
    }, {
      axisY: {
        labelInterpolationFnc: value => this.formatPrice(value),
      },
    });
  }

  fetch() {
    APIRequest.V3.get(this.url).then((response) => {
      this.success(response);
    });
  }

  success(model) {
    this.setState({ model });

    if (this.hasMinimumSummaries(model)) {
      this.buildChart(model);
    }
  }

  hasMinimumSummaries(model) {
    return model &&
      model.summaries &&
      model.summaries.length >= MIN_SUMMARIES;
  }

  shouldRenderTransactions() {
    return this.hasMinimumSummaries(this.state.model);
  }

  fetchAndRenderChart() {
    if (!this.chart) {
      this.fetch();
    }
  }

  renderChartContent() {
    return (
      <section
        className={
          classNames(
            'price-history-chart',
            {
              'price-history-chart--hidden': !this.shouldRenderTransactions(),
            },
          )
        }
      >
        <div className="price-history-chart__header">
          <div className="g-col-7">
            <h2 className="heading-3">{I18n.t('js.transactionHistory.chartHeader')}</h2>
          </div>
          <div className="g-col-5 align-right">
            <ul className="price-history-chart__legend">
              <li className="price-history-chart__label price-final">
                {I18n.t('js.transactionHistory.soldPrice')}
              </li>
            </ul>
          </div>
        </div>
        <div
          data-chart={`line-chart-${this.props.objectId}`}
          className="price-history-chart__line-chart"
        />
        <p className="fine-print muted mb-0">{I18n.t('js.transactionHistory.chartDescription')}</p>
      </section>
    );
  }

  render() {
    if (!this.props.objectId) {
      return null;
    }
    if (!this.shouldRenderTransactions()) {
      return null;
    }

    return this.renderChartContent();
  }
}

PriceGuideChart.propTypes = {
  objectId: PropTypes.number.isRequired,
  objectType: PropTypes.string,
  loadChart: PropTypes.bool,
};

PriceGuideChart.defaultProps = {
  loadChart: true,
};

export default PriceGuideChart;
